import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import { Link as RouterLink, useSearchParams } from 'react-router';

import { useEntitlements } from '../hooks/useEntitlements.context';
import type { UseEntitlementsResult } from '../hooks/useEntitlements';

/**
 * Build the list of "what you just unlocked" bullets for the success page.
 *
 * Four bullets are derived from `useEntitlements().features`
 * (`maxTotalUsers`, `maxActivePolicies`, `dataRetentionDays`) plus two static
 * tier benefits that are not modeled in the entitlements payload but are
 * canonical Standard-tier capabilities (self-serve billing, SSO on request).
 *
 * When entitlements are still loading or the permissive fallback returned
 * unlimited features (`null`), the numeric bullets fall back to generic
 * "Standard" copy so the page still reads as celebratory.
 */
function buildUnlockedFeatures(features: UseEntitlementsResult['features']): string[] {
    const items: string[] = [];

    // Total users — Standard ships with 200 base seats, expandable in user packs of 50 each (#1630).
    if (features?.maxTotalUsers != null) {
        items.push(`${features.maxTotalUsers} base users (expandable in packs of 50)`);
    } else {
        items.push('200 base users (expandable in packs of 50)');
    }

    // Active policies
    if (features?.maxActivePolicies != null) {
        items.push(`${features.maxActivePolicies} active policies`);
    } else {
        items.push('Unlimited active policies');
    }

    // Data retention — convert days to years (rounded) for readability.
    if (features?.dataRetentionDays != null) {
        const years = Math.round(features.dataRetentionDays / 365);
        items.push(`${years}-year retention`);
    } else {
        items.push('Long-term data retention');
    }

    items.push('Self-serve billing portal');
    items.push('SSO available on request');

    return items;
}

/**
 * Billing success landing page (issue #1877, refreshed in #1898).
 *
 * Stripe redirects the admin here after a successful checkout. The heavy
 * lifting (tier flip, subscription persistence) happens server-side via the
 * `checkout.session.completed` webhook — this page exists purely to confirm
 * the upgrade and steer the admin to their next high-value action.
 *
 * Structure:
 * 1. Celebratory hero band — large CheckCircle icon, "Welcome to Standard" title.
 * 2. Unlocked-features list — bullets derived from `useEntitlements().features`.
 * 3. Next-step CTAs — Invite teammates / Upload policy / Back to Settings.
 *
 * `session_id` is appended by Stripe (via the `{CHECKOUT_SESSION_ID}` placeholder
 * in our successUrl); we read it for observability only — the webhook is the
 * source of truth.
 */
function BillingSuccessPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id') ?? '';

    const { features } = useEntitlements();
    const unlockedFeatures = buildUnlockedFeatures(features);

    return (
        // #3417 — the horizontal gutter comes entirely from
        // AuthenticatedLayout's `<Box component="main" className="main-content
        // container">` (`p: { xs: '1.5rem 1rem', md: '2rem 20px' }` — the
        // emotion `sx` wins the cascade over index.css). This page previously
        // self-applied `p: { xs: 2, sm: 4 }` on top of that gutter, doubling
        // the inset. `maxWidth: 720` / `mx: 'auto'` stay — they're the
        // deliberate narrow reading column for this page, not part of the
        // double-padding bug. No `className="page"` here on purpose: `.page`
        // (index.css) adds `margin-bottom: 2rem`, a visual change beyond this
        // fix's scope, and this page has no `.page-header` sibling structure
        // for it to pair with.
        <Box data-testid="billing-success-page" data-session-id={sessionId} sx={{ maxWidth: 720, mx: 'auto' }}>
            {/* 1. Celebration hero band */}
            <Paper
                data-testid="billing-success-hero"
                sx={(theme) => ({
                    p: { xs: 3, sm: 5 },
                    mb: 3,
                    textAlign: 'center',
                    // Theme-aware success tint so dark mode adapts automatically.
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.success.light,
                    color: theme.palette.success.contrastText,
                })}
            >
                <CheckCircleIcon data-testid="billing-success-icon" sx={{ fontSize: 72, color: 'common.white', mb: 1 }} />
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    Welcome to Standard
                </Typography>
            </Paper>

            {/* Subscription-pending caption — preserved per acceptance criteria #6,
                but rendered as visually subordinate small/muted text. */}
            <Typography data-testid="billing-success-pending-copy" variant="caption" color="text.secondary" component="p" sx={{ mb: 3, textAlign: 'center' }}>
                Your subscription is active. Standard features unlock automatically as soon as Stripe confirms the payment (usually within a few seconds).
            </Typography>

            {/* 2. Unlocked-features list */}
            <Paper sx={{ p: { xs: 3, sm: 4 }, mb: 3 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                    What you just unlocked
                </Typography>
                <List data-testid="billing-success-features-list" disablePadding sx={{ '& .MuiListItem-root': { px: 0 } }}>
                    {unlockedFeatures.map((feature) => (
                        <ListItem key={feature} disableGutters>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckIcon color="success" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {/* 3. Next-step CTAs */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'center' }}>
                <Button
                    component={RouterLink}
                    to="/users"
                    variant="contained"
                    color="success"
                    size="large"
                    data-testid="billing-success-cta-invite"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Invite teammates
                </Button>
                <Button
                    component={RouterLink}
                    to="/policies"
                    variant="outlined"
                    size="large"
                    data-testid="billing-success-cta-upload"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Upload your first policy
                </Button>
                <Button
                    component={RouterLink}
                    to="/settings"
                    variant="text"
                    size="large"
                    data-testid="billing-success-back-to-settings"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Back to Settings
                </Button>
            </Stack>
        </Box>
    );
}

export default BillingSuccessPage;
